if (!customElements.get('reflection-element')) {
  class ReflectionElement extends HTMLElement {
    constructor() {
      super();
      this.original = null;
      this.reflection = null;
      this.observer = null;
    }

    connectedCallback() {
      this.opacity = this.dataset.opacity || '0.4';
      this.gap = this.dataset.gap || '0px';
      this.fade = this.dataset.fade || '100%';
      this.offsetMobile = parseInt(this.dataset.offsetMobile || '0', 10);
      this.offsetDesktop = parseInt(this.dataset.offsetDesktop || '0', 10);
      this.clipMobile = parseInt(this.dataset.clipMobile || '100', 10);
      this.clipDesktop = parseInt(this.dataset.clipDesktop || '100', 10);
      this.reflectionPhi = this.dataset.reflectionPhi != null ? parseFloat(this.dataset.reflectionPhi) * Math.PI / 180 : null;
      this.reflectionSkew = this.dataset.reflectionSkew ? parseFloat(this.dataset.reflectionSkew) : 0;
      this.reflectionPerspective = this.dataset.reflectionPerspective || '800px';
      this.reflectionScale = this.dataset.reflectionScale ? parseFloat(this.dataset.reflectionScale) : 1;
      this.reflectionBlur = this.dataset.reflectionBlur || '4px';

      this._mq = window.matchMedia('(max-width: 767px)');
      this._mqListener = () => this.applyReflectionStyles();
      this._mq.addEventListener('change', this._mqListener);

      this.style.position = 'relative';
      if (!this.style.display) this.style.display = 'inline-block';

      this.original = document.createElement('div');
      this.original.classList.add('reflection-original');
      while (this.firstChild) {
        this.original.appendChild(this.firstChild);
      }
      this.appendChild(this.original);

      this.reflection = document.createElement('div');
      this.reflection.classList.add('reflection-mirror');
      this.reflection.setAttribute('aria-hidden', 'true');
      this.appendChild(this.reflection);

      this.applyReflectionStyles();
      this.syncReflection();

      this.observer = new MutationObserver(() => this.syncReflection());
      this.observer.observe(this.original, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'src']
      });

      this.resizeObserver = new ResizeObserver(() => this.syncReflection());
      this.resizeObserver.observe(this.original);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this._mq?.removeEventListener('change', this._mqListener);
      this._mvListeners?.forEach(({ el, fn }) => el.removeEventListener('camera-change', fn));
    }

    applyReflectionStyles() {
      if (!this.reflection) return;

      const isMobile = this._mq?.matches;
      const offset = isMobile ? this.offsetMobile : this.offsetDesktop;
      const clip = isMobile ? this.clipMobile : this.clipDesktop;
      const clipStop = 100 - clip;

      Object.assign(this.reflection.style, {
        position: 'absolute',
        left: '0',
        top: `${-offset}px`,
        width: '100%',
        transform: this.reflectionSkew
          ? `perspective(${this.reflectionPerspective}) rotateX(${this.reflectionSkew}deg) scale(${this.reflectionScale}, -${this.reflectionScale})`
          : `scale(1, -1)`,
        transformOrigin: 'center bottom',
        pointerEvents: 'none',
        userSelect: 'none',
        opacity: this.opacity
      });

      const fadeEdge = Math.min(clipStop + 15, 100);
      const mask = clip < 100
        ? `linear-gradient(to bottom, transparent ${clipStop}%, rgba(0,0,0,1) ${fadeEdge}%, rgba(0,0,0,1), transparent ${this.fade})`
        : `linear-gradient(to bottom, rgba(0,0,0,1), transparent ${this.fade})`;
      this.reflection.style.webkitMaskImage = mask;
      this.reflection.style.maskImage = mask;
    }

    syncReflection() {
      if (!this.original || !this.reflection) return;

      const activeEl = [...this.original.querySelectorAll('[data-show]')]
        .find(el => el.style.display !== 'none');
      const has3D = activeEl?.querySelector('model-viewer, template[data-lazy-model]');
      if (!has3D) {
        this.reflection.style.display = 'none';
        return;
      }
      this.reflection.style.display = '';

      const inner = document.createElement('div');
      inner.style.cssText = `width:100%;height:100%;filter:blur(${this.reflectionBlur})`;
      inner.innerHTML = this.original.innerHTML;

      // Strip src from reflection model-viewers — the GLB will be in cache by the time
      // the original loads, so the deferred fetch is near-instant.
      inner.querySelectorAll('model-viewer').forEach(mv => {
        const src = mv.getAttribute('src');
        if (src) {
          mv.setAttribute('data-deferred-src', src);
          mv.removeAttribute('src');
        }
      });

      this.reflection.innerHTML = '';
      this.reflection.appendChild(inner);
      this.reflection.style.width = '100%';
      this.reflection.style.height = '100%';
      this.reflection.style.overflow = 'hidden';
      this.setupModelViewerSync();
    }

    setupModelViewerSync() {
      const originalViewers = [...this.original.querySelectorAll('model-viewer')];
      if (!originalViewers.length) return;

      if (typeof originalViewers[0].getCameraOrbit !== 'function') {
        customElements.whenDefined('model-viewer').then(() => this.setupModelViewerSync());
        return;
      }

      this._mvListeners?.forEach(({ el, fn }) => el.removeEventListener('camera-change', fn));
      this._mvListeners = [];

      const reflectionViewers = [...this.reflection.querySelectorAll('model-viewer')];

      originalViewers.forEach((originalMv, i) => {
        const reflectionMv = reflectionViewers[i];
        if (!reflectionMv) return;

        reflectionMv.removeAttribute('camera-controls');
        reflectionMv.setAttribute('interaction-prompt', 'none');

        if (this.reflectionPhi !== null) {
          const phiDeg = this.reflectionPhi * 180 / Math.PI;
          reflectionMv.setAttribute('min-camera-orbit', `auto ${phiDeg}deg auto`);
          reflectionMv.setAttribute('max-camera-orbit', `auto ${phiDeg}deg auto`);
        }

        const fn = () => {
          const orbit = originalMv.getCameraOrbit();
          const target = originalMv.getCameraTarget();
          const fov = originalMv.getFieldOfView();
          const phi = this.reflectionPhi !== null ? this.reflectionPhi : orbit.phi;
          reflectionMv.cameraOrbit = `${orbit.theta}rad ${phi}rad ${orbit.radius}m`;
          reflectionMv.cameraTarget = `${target.x}m ${target.y}m ${target.z}m`;
          reflectionMv.fieldOfView = `${fov}deg`;
          reflectionMv.jumpCameraToGoal();
        };

        originalMv.addEventListener('camera-change', fn);
        this._mvListeners.push({ el: originalMv, fn });

        // Load reflection model from cache once the original finishes loading.
        // model-viewer has no .loaded property — we track it ourselves via dataset.mvLoaded.
        const deferredSrc = reflectionMv.getAttribute('data-deferred-src');
        if (deferredSrc) {
          const loadFromCache = () => {
            if (!reflectionMv.isConnected) return;
            reflectionMv.setAttribute('src', deferredSrc);
            reflectionMv.addEventListener('load', () => {
              reflectionMv.classList.add('model-loaded');
            }, { once: true });
          };
          if (originalMv.dataset.mvLoaded) {
            loadFromCache();
          } else {
            originalMv.addEventListener('load', () => {
              originalMv.dataset.mvLoaded = '1';
              loadFromCache();
            }, { once: true });
          }
        }
      });
    }
  }

  customElements.define('reflection-element', ReflectionElement);
}